import type { FormEvent } from "react";
import { useNavigate } from "react-router";

import type { FormValues } from "@prequel/import-react";
import { Alert, AlertKind, Button, ButtonStyle, Spinner } from "@prequel/shared-components";

import { DESTINATION_ROOT_KEY, useDestinationForm } from "./formState";

import { useCreateDestination, useUpdateDestination } from "@/api/import/destinations";
import ApiFormField from "@/components/ApiFormField";

type DestinationFormProps = {
  destinationId?: string;
  initialValues?: FormValues;
  cancelPath?: string;
};

const DestinationForm = ({
  destinationId,
  initialValues,
  cancelPath = "/import/destinations",
}: DestinationFormProps) => {
  const navigate = useNavigate();
  const createDestination = useCreateDestination();
  const updateDestination = useUpdateDestination();
  const isEditing = !!destinationId;
  const form = useDestinationForm({ initialValues, mode: isEditing ? "edit" : "create" });

  const isMutating = createDestination.isPending || updateDestination.isPending;
  const isPreparingForm = form.isLoading || form.isRefetching;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isMutating || isPreparingForm) {
      return;
    }

    if (isEditing) {
      updateDestination.mutate({
        id: destinationId,
        destination: form.getDestinationUpdate(),
      });
      return;
    }

    createDestination.mutate(form.getDestination());
  };

  if (form.isLoading) {
    return <Spinner />;
  }

  return (
    <form className="space-y-8 pb-16" onSubmit={onSubmit}>
      {!!form.error && <Alert kind={AlertKind.ERROR}>Failed to generate destination form.</Alert>}

      <div className="space-y-6">
        {form.fields.map((field) => (
          <ApiFormField
            key={field.path}
            field={field}
            values={form.draft}
            setFieldValue={form.setFieldValue}
            rootKey={DESTINATION_ROOT_KEY}
            disabled={isMutating}
            isEditing={isEditing}
          />
        ))}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type={ButtonStyle.TERTIARY} text="Cancel" onClick={() => navigate(cancelPath)} disabled={isMutating} />
        <Button
          type={ButtonStyle.PRIMARY}
          disabled={isMutating || isPreparingForm}
          submit
          text={
            isMutating ? (
              <div className="flex">
                <Spinner.Inline className="mr-3 text-white" />
                {isEditing ? "Updating Destination..." : "Saving Destination..."}
              </div>
            ) : isEditing ? (
              "Update Destination"
            ) : (
              "Save Destination"
            )
          }
        />
      </div>
    </form>
  );
};

export default DestinationForm;
